import { useEffect, useState } from 'react';
import { fetchRateCard, type RateCardView } from '@frontend/BidSheets/bidService';
import { useFinancialVisible } from '@frontend/BidSheets/financialDisplay';

/**
 * Read-only MSA rates on the Commercial tab (gap review 2.4; design §4 D.4 "keep MsaRateCard on
 * real data"). The rows are the bid's FROZEN rate-card snapshot — the rates this project was
 * priced at — never the live catalog: an admin repricing the catalog must not rewrite what a
 * running project reads as its own commercial terms (the snapshot invariant, verbatim).
 *
 * Money boundaries, the MarginPanel pattern exactly: the route 403s a blind caller, surfaced
 * here as the panel simply not rendering — nothing to grey out. A pre-snapshot bid (404, no
 * frozen card) also renders nothing rather than a lying empty table.
 */
interface MsaRatesPanelProps {
  /** The bid's UUID (matters.bid_id) — absent on a non-converted matter. */
  bidId: string | null | undefined;
}

const rate = (v: string | null | undefined): string => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  // Up to 4 fraction digits: rates freeze at NUMERIC(14,4), and rounding a frozen commercial
  // term to cents would display a rate the bid was never priced at ($85.125 is not $85.13).
  return Number.isFinite(n)
    ? n.toLocaleString('en-US', {
        style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 4,
      })
    : '—';
};

const MsaRatesPanel = ({ bidId }: MsaRatesPanelProps): JSX.Element | null => {
  const financialVisible = useFinancialVisible();
  const [card, setCard] = useState<RateCardView | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Reset FIRST, and store whatever resolves — including null. Keeping the old card while a
    // new bidId resolves to 403/404 left project A's rates rendering against project B when the
    // detail re-rendered in place (review): a frozen card is only true of the bid it froze for.
    setCard(null);
    if (!bidId || !financialVisible) return undefined;
    void fetchRateCard(bidId).then((loaded) => {
      if (!cancelled) setCard(loaded);
    });
    return () => { cancelled = true; };
  }, [bidId, financialVisible]);

  if (!financialVisible || !card) return null;

  const equipment = card.equipment ?? [];
  const labour = card.labour ?? [];

  return (
    <section className="pipeline-panel">
      <h2 className="pipeline-panel-title">MSA rates</h2>
      <p className="pipeline-panel-subtitle">
        Frozen at bid creation — the rates this project was priced at. In-scope work prices from
        ISR and the labour rate; force-account work prices from OSR and the labour FA rate.
        Catalog edits reach new bids only.
        {/* The third-surface footnote (P2 Build M, decision 4): FA can price from the client
            organization's MSA card, so this frozen card is no longer the only rate surface a
            reader might reconcile an FA line against — each line names its source. */}
        {' '}Force-account pricing may resolve through the client organization&apos;s own MSA
        card when one is on file — each FA line names its rate source.
      </p>
      {equipment.length > 0 && (
        <table className="pipeline-table">
          <thead>
            <tr><th>Equipment</th><th>Category</th><th>Unit</th><th>ISR</th><th>OSR</th></tr>
          </thead>
          <tbody>
            {equipment.map((row) => (
              <tr key={row.id}>
                <td>{row.name}</td>
                <td>{row.category ?? '—'}</td>
                <td>{row.unit}</td>
                <td>{rate(row.default_isr)}</td>
                <td>{rate(row.default_osr)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {labour.length > 0 && (
        <table className="pipeline-table">
          <thead>
            <tr><th>Labour role</th><th>Unit</th><th>Rate</th><th>FA rate</th></tr>
          </thead>
          <tbody>
            {labour.map((row) => (
              <tr key={row.id}>
                <td>{row.name}</td>
                <td>{row.unit}</td>
                <td>{rate(row.default_rate)}</td>
                <td>{rate(row.default_fa_rate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {equipment.length === 0 && labour.length === 0 && (
        <p className="pipeline-muted">The frozen card holds no equipment or labour rates.</p>
      )}
    </section>
  );
};

export default MsaRatesPanel;
